import {
  Box,
  CircularProgress,
  Stack,
  Switch,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { makeStyles, styled } from "@mui/styles";
import { useDispatch, useSelector } from "react-redux";
import { ReactComponent as Round } from "../../assets/admin/svg/org-image.svg";
import VSNTypography from "../../shared/VSNTypography";
import { ApiContainer } from "../../utils/api";
import VSNButton from "../../shared/VSNButton";
import { toggleModelShow } from "../../shared/CustomFunc";

const useStyles = makeStyles((theme) => ({
  heading: {
    position: "sticky",
    top: 0,
    borderBottom: "1px solid var(--primary)",
    padding: "12px 25px",
    margin: "0 -25px",
    backgroundColor: "var(--white)",
    zIndex: 2,
    [theme.breakpoints.down("sm")]: {
      padding: "12px 15px",
      margin: "0 -15px",
    },
    "&:after": {
      position: "absolute",
      content: "''",
      left: 0,
      right: 0,
      height: 25,
      width: "100%",
      bottom: "100%",
      background: "var(--white)",
      zIndex: 8,
    },
    "& svg": {
      borderRadius: "50%",
      [theme.breakpoints.down("md")]: {
        width: 40,
        height: 40,
      },
      [theme.breakpoints.down("sm")]: {
        width: 30,
        height: 30,
      },
    },
    "& h5": {
      fontSize: 24,
      fontWeight: 700,
      lineHeight: "31px",
      marginLeft: 16,
      [theme.breakpoints.down("md")]: {
        fontSize: 18,
        lineHeight: "26px",
        maxWidth: "70%",
      },
    },
  },
  buttons: {
    display: "flex",
    justifyContent: "flex-start",
    alignItems: "center",
    backgroundColor: "#F6F6FD",
    padding: "20px 25px",
    gap: 10,
    marginLeft: "-25px",
    marginRight: "-25px",
    position: "sticky",
    bottom: 0,
    zIndex: 99,
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      padding: "15px",
      gap: 10,
      marginLeft: "-15px",
      marginRight: "-15px",
    },
    "& .MuiButtonBase-root": {
      margin: 0,
      padding: "10px 25px",
      lineHeight: "21px",
      width: "auto",
      borderRadius: 10,
      "& span": {
        fontSize: 16,
        fontWeight: 400,
      },
      [theme.breakpoints.down("sm")]: {
        width: "100%",
      },
    },
  },
  createLinkButton: {
    "&.MuiButtonBase-root": {
      color: "var(--white)",
    },
  },
  expandTableWrapper: {
    overflowY: "auto",
    margin: "0 -24px",
    "& .MuiPaper-root": {
      margin: "0 !important",
      padding: "0 !important",
      "&.MuiTableContainer-root": {
        maxHeight: "100%",
      },
    },
    "& .MuiTableCell-root": {
      textAlign: "left",
    },
    "& th": {
      zIndex: 1,
    },
    "& tbody": {
      "& .MuiTableCell-root": {
        padding: "10px 16px",
        "&:first-child": {
          width: "80%",
        },
      },
    },
  },
}));

const AntSwitch = styled(Switch)(({ theme }) => ({
  "&.MuiSwitch-root": {
    width: 28,
    height: 16,
    padding: 0,
    display: "flex",
    "&:active": {
      "& .MuiSwitch-thumb": {
        width: 15,
      },
      "& .MuiSwitch-switchBase.Mui-checked": {
        transform: "translateX(9px)",
      },
    },
    "& .MuiSwitch-switchBase": {
      padding: 2,
      "&.Mui-checked": {
        transform: "translateX(12px)",
        color: "var(--white)",
        "& + .MuiSwitch-track": {
          opacity: 1,
          background: "var(--Tertiary)",
        },
        "&.Mui-disabled + .MuiSwitch-track": {
          opacity: 0.3,
          background: "var(--Tertiary)",
        },
      },
      "& + .MuiSwitch-track": {
        background: "#00000040",
      },
    },
    "& .MuiSwitch-thumb": {
      boxShadow: "0 2px 4px 0 rgb(0 35 11 / 20%)",
      width: 12,
      height: 12,
      borderRadius: 6,
      transition: "all 0.4s ease-in-out",
    },
    "& .MuiSwitch-track": {
      borderRadius: 10,
      opacity: 1,
      boxSizing: "border-box",
    },
  },
}));

export default function WaiverPreference({
  waivers,
  isLoading,
  onSwitchChange,
}) {
  const classes = useStyles();
  const { api } = ApiContainer();
  const dispatch = useDispatch();

  return (
    <>
      <Stack direction="row" alignItems="center" className={classes.heading}>
        <Stack>
          <Round />
        </Stack>
        <VSNTypography variant="h5" component="h5">
          Waivers Preferences
        </VSNTypography>
      </Stack>
      {isLoading ? (
        <Box
          sx={{
            textAlign: "center",
            height: 45,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CircularProgress size={24} color="inherit" />
        </Box>
      ) : (
        <Box className={classes.expandTableWrapper}>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <strong>Waivers</strong>
                  </TableCell>
                  <TableCell align="center">
                    <strong>Access?</strong>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {waivers?.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell>{item?.name || "N/A"}</TableCell>
                    <TableCell align="center">
                      <AntSwitch
                        sx={
                          item?.nonChangeable ? { pointerEvents: "none" } : {}
                        }
                        disabled={item?.nonChangeable}
                        checked={item?.is_assign || false}
                        onChange={(event) =>
                          !item?.nonChangeable
                            ? onSwitchChange(event, item)
                            : null
                        }
                        inputProps={{ "aria-label": "ant design" }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
      <Stack
        direction="row"
        flexWrap="wrap"
        gap={2}
        className={classes.buttons}
      >
        <VSNButton
          handleClick={() => {
            dispatch(toggleModelShow("createOrg"));
          }}
          className={classes.createLinkButton}
        >
          Save
        </VSNButton>
      </Stack>
    </>
  );
}
